/**
 * The coaching flow: start a set, then a verdict on every rep as it happens.
 *
 * This is the app's main interface, and it is a different job from the capture
 * controls in `controls.js`. Capturing a fixture is an occasional errand for
 * whoever is tuning the detector; being told whether the rep you just did was
 * good is the thing the athlete opened the app for. So the recorder's buttons —
 * including "Mark rep", which only ever existed to label ground truth for
 * tuning — move behind `?capture=1`, and this takes the bottom bar.
 *
 * Why a verdict per rep and not only the running tally: a tally answers "how did
 * that set go", which is a question for afterwards. Mid-set the athlete needs to
 * know what to change on the next rep, and that is one short cue delivered
 * immediately. Both are shown; they answer different questions.
 *
 * DOM rather than canvas, for the reasons `controls.js` gives: the canvas context
 * is mirrored so text painted into it comes out backwards, and real buttons get
 * touch targets and screen-reader semantics for free.
 */

import { repFault, repGood, soundCheck, tone, unlockAudio } from "../audio";

/**
 * How long a verdict stays up when no further rep replaces it.
 *
 * A verdict belongs on screen until the next one replaces it. This was 2500ms,
 * then briefly 1200ms with the banner also coming down partway into the next
 * rep, on the reasoning that a banner outliving its rep reads as a verdict on
 * the one being performed. The athlete tried it and the opposite was true: the
 * cues went by faster than he could read them from where he was standing, which
 * is the whole problem the banner exists to solve. A cue he cannot read is worth
 * nothing, and the sound is what tells him which rep it was about anyway.
 *
 * So the next rep is what takes it down, and this is only the fallback for the
 * last rep of a set — long enough to read, short enough that it is gone before
 * he comes back to the phone.
 */
const VERDICT_MS = 8000;

/** Steps in a bonus counter, and what one step is worth by default. */
const BONUS_STEPS = 10;

/** At most this many cues at once; more is not coaching, it is a list. */
const MAX_CUES = 2;

/**
 * What the athlete should be told about one rep, as data.
 *
 * Separate from the rendering so it can be checked against a recording without a
 * browser: feeding the reference ten-rep take through this is how the cues were
 * confirmed to say something sensible on every rep.
 *
 * @param scored Rows from the exercise's `scoreRep`. A row whose `pass` is null was not
 *   judged — the landmarks it needs were guesses — so it is neither a pass nor a
 *   failure and cannot produce a cue. A row marked `informational` is dropped
 *   before any of that: it is a figure to read, not a judgement, so it can
 *   neither fail a rep nor make one count as only partly seen.
 * @param cues What to say when a criterion fails, by criterion id, from the
 *   exercise definition. Each one is imperative and short, because it is read
 *   mid-rep from across a room, and must not overstate what was measured — the
 *   swing's "Head level" rather than "eyes forward", since head pitch is
 *   measured and gaze is not something the landmarks report. A criterion with no
 *   cue falls back to its own label, so a new exercise is never silent.
 */
export function verdictFor(scored, cues) {
  const labels = cues || {};
  const judged = scored.filter((row) => !row.informational);
  const failed = judged.filter((row) => row.pass === false);
  const unjudged = judged.filter((row) => row.pass === null);
  if (failed.length > 0) {
    return {
      good: false,
      text: failed
        .slice(0, MAX_CUES)
        .map((row) => labels[row.id] || row.label)
        .join(" · "),
      partial: false,
    };
  }
  return {
    good: true,
    text: "Good",
    // Said plainly rather than passed off as a clean rep: if the feet were out
    // of frame, the athlete should know the verdict only covers part of it.
    partial: unjudged.length > 0,
  };
}

export class CoachControls {
  /**
   * @param handlers.onStart Called when the set begins, to reset the counters.
   * @param handlers.onStop Called when the set ends.
   * @param handlers.onMenu Back to the drill list. Mid-set there is no way
   *   back by design — Stop first — but before one has started, a drill picked
   *   by mistake must not need a reload to undo.
   * @param handlers.canStart Whether the bar may act at all. The menu and the
   *   receipt both sit over this bar and both own the keyboard while they are
   *   up: a `keydown` listener on `window` still fires for them, and Space
   *   choosing a drill must not also start a set behind it.
   * @param handlers.countdownSeconds Delay before the set begins, so the phone
   *   can be propped up and the athlete can walk into frame.
   */
  constructor({
    onStart,
    onStop,
    countdownSeconds = 0,
    sound = true,
    soundcheck = false,
    canStart = () => true,
    onMenu = null,
  }) {
    this.onStart = onStart;
    this.onStop = onStop;
    this.canStart = canStart;
    /** Set by `setExercise`; until then there is no drill to start. */
    this.exercise = null;
    this.countdownSeconds = countdownSeconds;
    this.sound = sound;
    this.soundcheck = soundcheck;
    this.remaining = 0;
    this.ticker = null;
    /** Whether this drill is a hold rather than a set of reps. */
    this.timed = false;
    /** The settled state of the hold under way, for a timed drill. */
    this.holdState = null;
    /** Holds completed in this set, for the banner mid-hold. */
    this.holdNumber = 0;
    /** When the countdown is due to end, on the wall clock. See startCountdown. */
    this.deadline = null;
    this.verdictTimer = null;
    this.running = false;

    this.countdown = document.getElementById("countdown");
    this.countdownNumber = document.getElementById("countdown-number");
    this.countdownHint = document.getElementById("countdown-hint");
    this.verdict = document.getElementById("verdict");
    this.verdictRep = document.getElementById("verdict-rep");
    this.verdictText = document.getElementById("verdict-text");
    this.count = document.getElementById("coach-count");
    this.bonusNode = document.getElementById("coach-bonus");
    /**
     * Running totals for the drill's `aggregate: "sum"` rows, by id. Nothing
     * here is specific to the jump: an exercise that reports a figure worth
     * adding up gets a counter, and one that reports none has no counter. The
     * receipt totals the same rows at the end of the workout (see score.js).
     */
    this.bonus = [];
    this.button = document.getElementById("coach-toggle");
    this.menuButton = document.getElementById("coach-menu");
    this.status = document.getElementById("coach-status");

    this.button.addEventListener("click", () => this.toggle());
    // The countdown covers the whole screen, the bar included, so the Cancel
    // the button is offering at that moment cannot be reached: it is behind an
    // overlay at a higher layer. Tapping the countdown itself is the way out,
    // and it is the whole screen rather than a target to find — which is the
    // right size for something tapped on the way back into frame.
    this.countdown.addEventListener("click", () => {
      if (this.countingDown) {
        this.cancelCountdown();
      }
    });
    if (this.menuButton) {
      this.menuButton.addEventListener("click", () => {
        if (onMenu && !this.running && !this.countingDown) {
          onMenu();
        }
      });
    }
    window.addEventListener("keydown", (event) => {
      if (event.repeat || isTypingTarget(event.target)) {
        return;
      }
      // Enter and Space both, because whichever of them a given browser fires
      // on a focused button, the other should still work from anywhere.
      if (event.code === "Space" || event.key === "Enter") {
        event.preventDefault();
        this.toggle();
      }
    });

    this.render();
  }

  get countingDown() {
    return this.ticker != null;
  }

  /**
   * Points the bar at a drill. Called when one is chosen from the menu, and
   * again on returning to it, so the cues and the status line follow whichever
   * exercise is actually being scored.
   */
  setExercise(exercise) {
    this.exercise = exercise;
    // A hold is not a set of reps: the headline number is the clock rather than
    // a tally, and the verdict is a change of state rather than something that
    // happened once. See exercises/hold.js.
    this.timed = exercise != null && exercise.timed === true;
    this.holdState = null;
    this.holdNumber = 0;
    this.setCount(this.timed ? "0.0s" : 0);
    this.resetBonus();
    this.clearVerdict();
    this.render();
  }

  /**
   * The state of a hold, every frame, for a drill that has one.
   *
   * The athlete is upside down and cannot read anything, so the sound is the
   * whole feature and the banner is for afterwards. The tones are the rep tones
   * on purpose: there are two of them, they already mean good and not good in
   * this app, and a third pair would be one more thing to learn mid-handstand
   * for no gain. The counter settles the state before it reaches here
   * (`settleMs`), so a reading flickering at the threshold is not a sound.
   *
   * @param state "good", "bad", or null when no hold is under way.
   * @param seconds Seconds in line so far, for the headline figure.
   */
  showHoldState(state, seconds) {
    if (this.timed) {
      this.setCount(`${seconds.toFixed(1)}s`);
    }
    if (state === this.holdState) {
      return;
    }
    const previous = this.holdState;
    this.holdState = state;
    // The hold is over. Nothing is announced — its verdict arrives on its own as
    // a completed hold — but the banner has to come down, and for one build it
    // did not: it says what is true right now and has no timer, so coming out of
    // a handstand left a green IN LINE sitting over a body folded in half on the
    // floor. A hold that was too short to count banks no verdict to replace it
    // either, so clearing it here is the only thing that does. An accepted hold
    // overwrites this in the same frame, so there is nothing to see in between.
    if (state == null) {
      this.clearVerdict();
      return;
    }
    // And nothing is announced as one opens: the athlete is mid kick-up and is
    // not being told off for it, so the first change out of that is the first
    // thing heard.
    if (previous == null) {
      return;
    }
    const cues = (this.exercise && this.exercise.stateCues) || {};
    const good = state === "good";
    this.verdictRep.textContent = `Hold ${this.holdNumber + 1}`;
    this.verdictText.textContent = cues[state] || (good ? "Good" : "Fix it");
    this.verdict.className = good ? "good" : "bad";
    this.verdict.hidden = false;
    // No timer: unlike a rep's verdict this says what is true right now, so it
    // comes down when it stops being true rather than after a while.
    clearTimeout(this.verdictTimer);
    if (this.sound) {
      if (good) {
        repGood();
      } else {
        repFault();
      }
    }
  }

  toggle() {
    if (!this.canStart() || this.exercise == null) {
      return;
    }
    // Inside the gesture, which is the only moment the browser will let audio
    // start. Without this the rep tones — which fire on their own, long after any
    // tap — are silently dropped on iOS, where a context created outside a
    // gesture stays suspended for good.
    if (this.sound) {
      unlockAudio();
    }
    if (this.soundcheck) {
      // ?soundcheck=1 turns Start into a speaker test and nothing else: hearing
      // the fault sound otherwise means performing a bad rep on purpose, which
      // is how the first version shipped inaudible on a phone.
      soundCheck();
      return;
    }
    if (this.running) {
      this.stop();
    } else if (this.countingDown) {
      this.cancelCountdown();
    } else if (this.countdownSeconds > 0) {
      this.startCountdown();
    } else {
      this.begin();
    }
  }

  /**
   * Counts in from a deadline rather than by counting ticks.
   *
   * The ticks are not reliable on the one device this is used on. The athlete
   * presses Start and walks away, and a phone that locks or a tab iOS throttles
   * in the background stops firing timers — so a countdown that reaches zero by
   * subtracting one per tick does not reach zero at all, and the overlay sits
   * there over a bar it covers, with the set never starting and a reload the
   * only way out. Read off the clock, a tick that arrives late or never simply
   * finds the time already gone and starts the set.
   */
  startCountdown() {
    this.deadline = Date.now() + this.countdownSeconds * 1000;
    this.remaining = this.countdownSeconds;
    this.countdownHint.textContent = "Get into position… tap to cancel";
    // Faster than the second it displays, so the first tick after the screen
    // comes back is at most a fifth of a second late rather than a whole one.
    this.ticker = setInterval(() => this.tickCountdown(), 200);
    this.render();
  }

  tickCountdown() {
    const left = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
    if (left === this.remaining) {
      return;
    }
    this.remaining = left;
    if (left <= 0) {
      this.cancelCountdown();
      this.begin();
      return;
    }
    if (this.sound) {
      // Audible, so the athlete can already be facing away while it runs.
      tone(660, 0.08);
    }
    this.render();
  }

  cancelCountdown() {
    clearInterval(this.ticker);
    this.ticker = null;
    this.deadline = null;
    this.remaining = 0;
    this.render();
  }

  begin() {
    this.running = true;
    this.holdState = null;
    this.holdNumber = 0;
    this.setCount(this.timed ? "0.0s" : 0);
    this.clearVerdict();
    // A set starts the score at zero. Carrying it over would make the counter a
    // running total of the session, which the receipt already is.
    this.resetBonus();
    if (this.sound) {
      // Longer and higher than the ticks: this one means "now".
      tone(1040, 0.35);
    }
    this.onStart();
    this.render();
  }

  stop() {
    this.running = false;
    // The set is over and the receipt is about to cover the screen; a verdict
    // from the last rep hanging over it belongs to a set that has ended.
    this.clearVerdict();
    this.onStop();
    this.render();
  }

  /**
   * Shows the verdict for one rep.
   *
   * @param number Which rep this was.
   * @param scored Rows from `scoreRep()`.
   */
  showRep(number, scored) {
    const verdict = verdictFor(scored, this.exercise && this.exercise.cues);
    const noun = this.timed ? "Hold" : "Rep";
    this.verdictRep.textContent = verdict.partial
      ? `${noun} ${number} — partly seen`
      : `${noun} ${number}`;
    this.verdictText.textContent = verdict.text;
    this.verdict.className = verdict.good ? "good" : "bad";
    this.verdict.hidden = false;

    // The sound is the part that actually reaches the athlete. Filmed side-on
    // they stand at ninety degrees to the phone, so reading the screen costs a
    // turn of the head on every rep — a change to the movement being measured.
    // A hold has been sounding its changes of state as they happened, and the
    // one that ended it was the last of them. A second tone here would be the
    // same news twice.
    if (this.sound && !this.timed) {
      if (verdict.good) {
        repGood();
      } else {
        repFault();
      }
    }

    this.addBonus(scored);

    clearTimeout(this.verdictTimer);
    this.verdictTimer = setTimeout(() => this.clearVerdict(), VERDICT_MS);
    // The clock keeps the headline on a timed drill. The tally of holds is on
    // the banner and on the ticket, which is where a count belongs when the
    // number worth watching is the seconds.
    this.holdNumber = number;
    if (!this.timed) {
      this.setCount(number);
    }
  }

  clearVerdict() {
    this.verdict.hidden = true;
  }

  setCount(value) {
    this.count.textContent = String(value);
  }

  resetBonus() {
    this.bonus = [];
    this.renderBonus();
  }

  /**
   * Adds this rep's aggregate figures to the counter.
   *
   * A rep the drill could not measure contributes nothing rather than resetting
   * the counter or showing a gap: the score only ever grows, which is the whole
   * appeal of one.
   */
  addBonus(scored) {
    for (const row of scored) {
      if (row.aggregate !== "sum" || !Number.isFinite(row.value)) {
        continue;
      }
      let entry = this.bonus.find((b) => b.id === row.id);
      if (entry == null) {
        entry = {
          id: row.id,
          label: row.label,
          unit: row.unit,
          precision: row.precision,
          // What one step of the counter costs, in the row's own unit. The
          // drill knows; the bar only knows there are ten of them.
          step: row.bonusStep,
          total: 0,
        };
        this.bonus.push(entry);
      }
      entry.total += row.value;
    }
    this.renderBonus();
  }

  renderBonus() {
    if (this.bonusNode == null) {
      return;
    }
    if (this.bonus.length === 0) {
      this.bonusNode.hidden = true;
      this.bonusNode.textContent = "";
      this.bonusNode.className = "";
      return;
    }
    // One counter, drawn as steps rather than as the raw figure: a number of
    // seconds climbing by fractions is something to read, and ten cells filling
    // one at a time is something to notice while moving. The drill says what one
    // step costs; the scale and the ten steps are the bar's own.
    const entry = this.bonus[0];
    const step = entry.step > 0 ? entry.step : 1;
    // Whole steps earned, and never less than one once anything has been
    // banked: a counter that appears reading zero is not a reward.
    const level = Math.min(
      BONUS_STEPS,
      entry.total <= 0 ? 0 : Math.max(1, Math.floor(entry.total / step))
    );
    const maxed = level >= BONUS_STEPS;

    this.bonusNode.textContent = "";
    const label = document.createElement("span");
    label.className = "bonus-label";
    label.textContent = entry.label.toUpperCase();
    this.bonusNode.appendChild(label);
    const track = document.createElement("span");
    track.className = "bonus-track";
    for (let i = 0; i < BONUS_STEPS; i += 1) {
      const cell = document.createElement("span");
      if (i < level) {
        cell.className = "on";
      }
      track.appendChild(cell);
    }
    this.bonusNode.appendChild(track);
    const number = document.createElement("span");
    number.className = "bonus-level";
    number.textContent = maxed ? "MAX" : String(level);
    this.bonusNode.appendChild(number);

    this.bonusNode.hidden = false;
    this.bonusNode.setAttribute(
      "aria-label",
      `${entry.label} bonus ${level} of ${BONUS_STEPS}`
    );
    // Restarted rather than added: the classes are still on from the last rep.
    this.bonusNode.className = "";
    void this.bonusNode.offsetWidth;
    this.bonusNode.className = maxed ? "tick max" : "tick";
  }

  render() {
    this.button.textContent = this.running
      ? "Stop"
      : this.countingDown
        ? "Cancel"
        : "Start";
    this.button.setAttribute("aria-pressed", String(this.running));

    if (this.menuButton) {
      // Not while a set is running or counting in: leaving then would throw away
      // reps the athlete is in the middle of earning.
      this.menuButton.hidden = this.running || this.countingDown;
    }

    this.countdown.hidden = !this.countingDown;
    if (this.countingDown) {
      this.countdownNumber.textContent = String(this.remaining);
    }

    if (this.soundcheck) {
      this.status.textContent = "Sound check: good, then fault";
      this.button.textContent = "Play both sounds";
      return;
    }
    const name = this.exercise ? this.exercise.name : "";
    if (this.running) {
      this.status.textContent = `Counting ${name.toLowerCase()}…`;
    } else if (this.countingDown) {
      this.status.textContent = "Starting…";
    } else if (this.exercise) {
      this.status.textContent = `${name} · press Start, then do your reps`;
    } else {
      this.status.textContent = "Choose a drill";
    }
  }
}

/** So the shortcut does not fire while someone is typing in a field. */
function isTypingTarget(target) {
  if (target == null) {
    return false;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}
